import { BeeCustomFont } from 'modules/domains/template';
import { MessageMedium } from 'modules/graphql/types';
import { deleteRaw, getRaw, postRaw, putRaw } from 'services/api';

export const GET_BEE_AUTH_TOKEN_URL = '/templates/initBuilder';
export const GET_BEE_CUSTOM_FONTS_URL = '/templates/getProjectBeeEditorCustomFonts';
export const GET_MERGE_PARAMS_URL = '/i/templates/mergeParams?';
export const GET_TEMPLATE_DATA_URL = '/templates/templateData.json?';
export const UPDATE_BEE_CUSTOM_FONTS_URL = '/templates/updateBeeEditorCustomFonts';
export const FETCH_SNIPPETS_URL = '/i/snippets';
export const FETCH_SNIPPET_NAMES_URL = '/i/snippets/names';
export const FETCH_FEEDS_URL = '/integrations/dataFeeds.json';
export const POST_MESSAGE_TO_SELF_URL = '/i/sendToSelf';
export const FETCH_EMAIL_LISTS = '/i/lists/getListsforProofs';
export const POST_MESSAGE_TO_INTERNAL_URL = '/i/sendToInternalList';
export const POST_MESSAGE_AS_RANDOM_URL = '/i/sendToRandomUsersInList';
export const POST_MESSAGE_TO_ADDRESS = '/i/sendToEmail';
export const POST_MESSAGE_TO_USER_ID = '/i/sendToUserId';
const PUT_DISPLAY_MESSAGE_TO_INTERNAL_LIST_PATH = '/embedded-messaging/proofs';
const PUT_DISPLAY_MESSAGE_TO_SELF_PATH = '/embedded-messaging/proofs/self';
const PUT_DISPLAY_MESSAGE_TO_ADDRESSES_PATH = '/embedded-messaging/proofs/addresses';
const DELETE_DISPLAY_MESSAGE_TO_INTERNAL_LIST_PATH = '/embedded-messaging/proofs';
const DELETE_DISPLAY_MESSAGE_TO_SELF_PATH = '/embedded-messaging/proofs/user';
const DELETE_DISPLAY_MESSAGE_TO_ADDRESSES_PATH = '/embedded-messaging/proofs/addresses';

export const getBeeAuthToken = () => postRaw(GET_BEE_AUTH_TOKEN_URL, {});

export const getBeeCustomFonts = () => getRaw(GET_BEE_CUSTOM_FONTS_URL);

export const getMergeParams = () => {
  return getRaw(GET_MERGE_PARAMS_URL);
};

export const getMergeParamsByMedium = async <T = any>(messageMedium?: MessageMedium) =>
  (await getRaw<T>(GET_MERGE_PARAMS_URL, { params: { messageMedium } })).data;

export const getSnippetNamesRequest = async (): Promise<string[]> =>
  (await getRaw(FETCH_SNIPPET_NAMES_URL)).data;

export const getTemplateData = (templateId: string, locale?: string) => {
  let url = `${GET_TEMPLATE_DATA_URL}templateId=${templateId}`;
  if (locale) {
    url = `${url}&locale=${locale}`;
  }

  return getRaw(url);
};

export const updateBeeEditorCustomFonts = (fonts: BeeCustomFont[]) => {
  return postRaw(UPDATE_BEE_CUSTOM_FONTS_URL, { fonts });
};

export const fetchSnippetsRequest = (id?: number) => {
  const url = id ? `${FETCH_SNIPPETS_URL}/${id}` : FETCH_SNIPPETS_URL;
  return getRaw(url);
};

export const fetchFeedsRequest = () => getRaw(FETCH_FEEDS_URL);

const postTestMessage = (
  basePath: string,
  templateId?: number,
  selectedListId?: number,
  recipientEmail?: string,
  recipientUserId?: string,
  locale?: string,
  payload?: Record<string, unknown>,
) => {
  const qs = [];
  const definedPayload = !payload ? {} : payload;
  if (templateId) {
    qs.push(`templateId=${templateId}`);
  }
  if (selectedListId) {
    qs.push(`emailListId=${selectedListId}`);
  }
  if (recipientEmail) {
    qs.push(`emailsCsv=${recipientEmail}`);
  }
  if (recipientUserId) {
    qs.push(`userIdsCsv=${recipientUserId}`);
  }
  if (locale) {
    qs.push(`locale=${locale}`);
  }
  return postRaw(`${basePath}?${qs.join('&')}`, definedPayload);
};

export const postMessageToSelf = (templateId: number, locale?: string) =>
  postTestMessage(
    POST_MESSAGE_TO_SELF_URL,
    templateId,
    undefined,
    undefined,
    undefined,
    locale,
    undefined,
  );

export const fetchEmailLists = () => getRaw(FETCH_EMAIL_LISTS);

export const postMessageToInternal = (
  selectedListId: number,
  templateId: number,
  locale?: string,
) =>
  postTestMessage(
    POST_MESSAGE_TO_INTERNAL_URL,
    templateId,
    selectedListId,
    undefined,
    undefined,
    locale,
    undefined,
  );

export const postMessageAsRandom = (
  selectedListId: number,
  templateId: number,
  recipientEmail: string,
  testSize: number,
) => {
  const payload = {
    emailListId: selectedListId,
    recipient: recipientEmail,
    templateId,
    testSize,
  };
  return postTestMessage(
    POST_MESSAGE_AS_RANDOM_URL,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    payload,
  );
};

export const postMessageToAddress = (templateId: number, recipientEmail: string, locale?: string) =>
  postTestMessage(
    POST_MESSAGE_TO_ADDRESS,
    templateId,
    undefined,
    recipientEmail,
    undefined,
    locale,
    undefined,
  );

export const postMessageToUserId = (templateId: number, recipientUserId: string, locale?: string) =>
  postTestMessage(
    POST_MESSAGE_TO_USER_ID,
    templateId,
    undefined,
    undefined,
    recipientUserId,
    locale,
    undefined,
  );

export const putDisplayTestMessageToInternal = (selectedListId: number, templateId: number) =>
  putRaw(PUT_DISPLAY_MESSAGE_TO_INTERNAL_LIST_PATH, {
    listId: selectedListId,
    templateId,
  });

export const putDisplayTestMessageToSelf = (email: string, templateId: number) =>
  putRaw(PUT_DISPLAY_MESSAGE_TO_SELF_PATH, {
    email,
    templateId,
  });

export const putDisplayTestMessageToAddresses = (emailsCsv: string, templateId: number) =>
  putRaw(PUT_DISPLAY_MESSAGE_TO_ADDRESSES_PATH, {
    emailsCsv,
    templateId,
  });

export const deleteDisplayTestMessageToInternal = (selectedListId: number, templateId: number) => {
  const qs = [`templateId=${templateId}`, `listId=${selectedListId}`];
  return deleteRaw(`${DELETE_DISPLAY_MESSAGE_TO_INTERNAL_LIST_PATH}?${qs.join('&')}`, { data: {} });
};

export const deleteDisplayTestMessageToSelf = (email: string, templateId: number) => {
  const qs = [`templateId=${templateId}`, `email=${encodeURIComponent(email)}`];
  return deleteRaw(`${DELETE_DISPLAY_MESSAGE_TO_SELF_PATH}?${qs.join('&')}`, { data: {} });
};

export const deleteDisplayTestMessageToAddresses = (emailsCsv: string, templateId: number) => {
  const qs = [`templateId=${templateId}`, `emailsCsv=${encodeURIComponent(emailsCsv)}`];
  return deleteRaw(`${DELETE_DISPLAY_MESSAGE_TO_ADDRESSES_PATH}?${qs.join('&')}`, { data: {} });
};
